import { FC, useContext, useEffect, useState, useRef } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification, showSuccessNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import { HrailRailCurve } from "../../../../../http/hrail/types/railCurves";
import { useHrailRailCurvesApi } from "../../../../../http/hrail/useRailCurves";
import { useHrailSystemsApi } from "../../../../../http/hrail/useSystems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";

interface RailCurveFormBody {
  item: SelectOption | null;
  nominalAngleDegrees: number | null;
  deflectionDegrees: number | null;
  plane: "horizontal" | "vertical";
  horizontalDeductionMm: number | null;
  internalDeductionMm: number | null;
  externalDeductionMm: number | null;
  junctionsRequired: number | null;
  supportsRequired: number | null;
  systemIds: string[];
  order: number | null;
  visibility: VisibilityScopeValue;
}

type SelectOption = {
  id: string;
  label: string;
};

interface RailCurveFormProps {
  railCurve?: HrailRailCurve;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
}

const RailCurveForm: FC<RailCurveFormProps> = ({ railCurve, isDisabled, isLoading, onClose, onCreated, onSaved }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const railCurveApi = useHrailRailCurvesApi();
  const systemApi = useHrailSystemsApi();
  const itemsApi = useItemsApi();
  const [itemOptions, setItemOptions] = useState<SelectOption[]>([]);
  const itemAborter = useRef(new AbortController());
  const systemsAborter = useRef(new AbortController());
  const itemSearchTerm = useRef("");
  const loadItemOptions = createLoadOptions(itemAborter, itemSearchTerm, (query, signal) =>
    itemsApi.getAdminItems(query, signal).then((items) => items.map(toItemOption)),
  );

  const defaultValues: RailCurveFormBody = {
    item: railCurve?.item ? toItemOption(railCurve.item) : null,
    nominalAngleDegrees: railCurve?.nominalAngleDegrees ?? 0,
    deflectionDegrees: railCurve?.deflectionDegrees ?? 0,
    plane: railCurve?.plane ?? "horizontal",
    horizontalDeductionMm: railCurve?.horizontalDeductionMm ?? (railCurve?.plane === "vertical" ? null : 0),
    internalDeductionMm: railCurve?.internalDeductionMm ?? null,
    externalDeductionMm: railCurve?.externalDeductionMm ?? null,
    junctionsRequired: railCurve?.junctionsRequired ?? 0,
    supportsRequired: railCurve?.supportsRequired ?? 0,
    systemIds: railCurve?.systems.map((system) => system.id) ?? [],
    order: railCurve?.order ?? 50,
    visibility: railCurve?.visibility ?? "internal",
  };

  const planeOptions: SelectOption[] = [
    { id: "horizontal", label: i18n.translation.common.horizontal },
    { id: "vertical", label: i18n.translation.common.vertical },
  ];

  useEffect(() => {
    loadItemOptions("").then(setItemOptions).catch(() => setItemOptions([]));
  }, []);

  const systems = systemApi.useAdminHrailSystems({ limit: 100 }, systemsAborter.current.signal);

  useEffect(() => {
    return () => {
      itemAborter.current.abort();
      systemsAborter.current.abort();
    };
  }, []);

  const onSubmit = () => async (formData: RailCurveFormBody) => {
    setIsSubmitting(true);

    try {
      if (railCurve) {
        await railCurveApi.updateAdminHrailRailCurve(railCurve.item.id, {
          nominalAngleDegrees: formData.nominalAngleDegrees ?? 0,
          deflectionDegrees: formData.deflectionDegrees ?? 0,
          plane: formData.plane,
          horizontalDeductionMm: formData.plane === "horizontal" ? formData.horizontalDeductionMm : null,
          internalDeductionMm: formData.plane === "vertical" ? formData.internalDeductionMm : null,
          externalDeductionMm: formData.plane === "vertical" ? formData.externalDeductionMm : null,
          junctionsRequired: formData.junctionsRequired ?? 0,
          supportsRequired: formData.supportsRequired ?? 0,
          systemIds: formData.systemIds,
          order: formData.order ?? 50,
          visibility: formData.visibility,
        });
        dispatch(showSuccessNotification(i18n.translation));
        onSaved?.();
        onClose?.();
      } else {
        const res = await railCurveApi.insertAdminHrailRailCurve({
          itemId: formData.item?.id ?? "",
          nominalAngleDegrees: formData.nominalAngleDegrees ?? 0,
          deflectionDegrees: formData.deflectionDegrees ?? 0,
          plane: formData.plane,
          horizontalDeductionMm: formData.plane === "horizontal" ? formData.horizontalDeductionMm : null,
          internalDeductionMm: formData.plane === "vertical" ? formData.internalDeductionMm : null,
          externalDeductionMm: formData.plane === "vertical" ? formData.externalDeductionMm : null,
          junctionsRequired: formData.junctionsRequired ?? 0,
          supportsRequired: formData.supportsRequired ?? 0,
          systemIds: formData.systemIds,
          order: formData.order ?? 50,
          visibility: formData.visibility,
        });
        dispatch(showSuccessNotification(i18n.translation));
        onCreated?.(res.id);
        onSaved?.();
        onClose?.();
      }
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<RailCurveFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [railCurve]);

        const systemIds = watch("systemIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <CustomAsyncSelect<SelectOption>
                      {...field}
                      inputId={labelId}
                      defaultOptions={itemOptions}
                      loadOptions={loadItemOptions}
                      isClearable={false}
                      isDisabled={isDisabled || isLoading || isSubmitting || !!railCurve}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.id}
                      isInvalid={isInvalid}
                      onChange={(value) => setValue("item", value ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="nominalAngleDegrees"
              control={control}
              rules={{ required: true, min: 1, max: 359 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.nominalAngle} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={1}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("nominalAngleDegrees", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="deflectionDegrees"
              control={control}
              rules={{ required: true, min: 1, max: 359 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.deflection} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={1}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("deflectionDegrees", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="plane"
              control={control}
              rules={{ required: true }}
              render={({ fieldState }) => (
                <FormField label={i18n.translation.common.plane} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <CustomSelect<SelectOption>
                      inputId={labelId}
                      isInvalid={isInvalid}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      options={planeOptions}
                      value={planeOptions.find((option) => option.id === watch("plane")) ?? null}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.id}
                      onChange={(option) => {
                        const plane = (option?.id ?? "horizontal") as "horizontal" | "vertical";
                        setValue("plane", plane);
                        if (plane === "horizontal") {
                          setValue("horizontalDeductionMm", watch("horizontalDeductionMm") ?? 0);
                          setValue("internalDeductionMm", null);
                          setValue("externalDeductionMm", null);
                        } else {
                          setValue("horizontalDeductionMm", null);
                          setValue("internalDeductionMm", watch("internalDeductionMm") ?? 0);
                          setValue("externalDeductionMm", watch("externalDeductionMm") ?? 0);
                        }
                      }}
                    />
                  )}
                </FormField>
              )}
            />
            {watch("plane") === "horizontal" ? (
              <Controller
                name="horizontalDeductionMm"
                control={control}
                rules={{ required: true, min: 0 }}
                render={({ field, fieldState }) => (
                  <FormField
                    label={i18n.translation.common.horizontalDeduction}
                    error={fieldState.error}
                    isOptional={false}
                  >
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={typeof field.value === "number" ? field.value : null}
                        isInvalid={isInvalid}
                        decimalScale={0}
                        allowNegative={false}
                        min={0}
                        disabled={isDisabled || isLoading || isSubmitting}
                        onBlur={(value) => setValue("horizontalDeductionMm", value)}
                      />
                    )}
                  </FormField>
                )}
              />
            ) : (
              <>
                <Controller
                  name="internalDeductionMm"
                  control={control}
                  rules={{ required: true, min: 0 }}
                  render={({ field, fieldState }) => (
                    <FormField
                      label={i18n.translation.common.internalDeduction}
                      error={fieldState.error}
                      isOptional={false}
                    >
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          id={labelId}
                          value={typeof field.value === "number" ? field.value : null}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          allowNegative={false}
                          min={0}
                          disabled={isDisabled || isLoading || isSubmitting}
                          onBlur={(value) => setValue("internalDeductionMm", value)}
                        />
                      )}
                    </FormField>
                  )}
                />
                <Controller
                  name="externalDeductionMm"
                  control={control}
                  rules={{ required: true, min: 0 }}
                  render={({ field, fieldState }) => (
                    <FormField
                      label={i18n.translation.common.externalDeduction}
                      error={fieldState.error}
                      isOptional={false}
                    >
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          id={labelId}
                          value={typeof field.value === "number" ? field.value : null}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          allowNegative={false}
                          min={0}
                          disabled={isDisabled || isLoading || isSubmitting}
                          onBlur={(value) => setValue("externalDeductionMm", value)}
                        />
                      )}
                    </FormField>
                  )}
                />
              </>
            )}
            <FormField label={i18n.translation.systems.plural}>
              <MultiCheckbox
                disabled={isDisabled || isLoading || isSubmitting}
                values={
                  systems.data?.items.map((system) => ({
                    id: system.id,
                    title: system.name,
                    selected: systemIds.includes(system.id),
                  })) ?? []
                }
                onUpdate={(id, checked) =>
                  setValue("systemIds", checked ? [...systemIds, id] : systemIds.filter((systemId) => systemId !== id))
                }
              />
            </FormField>
            <Controller
              name="junctionsRequired"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.junctionsRequired} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("junctionsRequired", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="supportsRequired"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.supportsRequired} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("supportsRequired", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

type ItemOptionSource = { id: string; code?: string | null; description?: string | null; name?: string | null };

const toItemOption = (item: ItemOptionSource): SelectOption => ({ id: item.id, label: itemLabel(item) });

export default RailCurveForm;
